import { trpc } from '@tencent/tpro_rmonitor_product_manager_user';
import { get } from 'lodash-es';
import { userImplement } from '@/config/hooks/user';
import AbstractHttpClient from '@/infrastructure/axios/request';
import { TrpcService } from '@/service/trpc';
import { WithTypeCast } from '@raft/utils';

type ICheckAdminReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckAdminReq>;
type IVerifyCodeReq = WithTypeCast<trpc.rmonitor.product_manager.IVerifyCodeReq>;
type ISendVerifyCodeReq = WithTypeCast<trpc.rmonitor.product_manager.ISendVerifyCodeReq>;
type ICheckUserInProductReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckUserInProductReq>;
type IUserInfo = WithTypeCast<trpc.rmonitor.product_manager.IUserInfo>;
type IGetUsersReq = WithTypeCast<trpc.rmonitor.product_manager.IGetUsersReq>;
type IGetUsersRsp = WithTypeCast<trpc.rmonitor.product_manager.IGetUsersRsp>;
type IQuitProductReq = WithTypeCast<trpc.rmonitor.product_manager.IQuitProductReq>;
type ICheckSuperAdminReq = WithTypeCast<trpc.rmonitor.product_manager.ICheckSuperAdminReq>;

export interface ProductInfo {
  productId: string;
  productName: string;
  appKey: string;
  platformId: string;
  type?: number;
  department?: string;
  isFlutter: boolean;
  logoUrl?: string;
  owner?: string;
  hasPermission?: boolean;
  bundleId?: string | null;
  diagnoseGuidAlias?: string;
  expireTime: string;
  diagnoseGuidMappingField?: string | null;
}

export class UserService extends AbstractHttpClient {
  constructor() {
    super({
      serviceName: TrpcService.UserService,
    });
  }

  public getUserInfo = async (): Promise<IUserInfo | null> => {
    try {
      const res = await this.trpcPost('GetUserInfo');

      return res.userInfo;
    } catch (e) {
      return null;
    }
  };

  /**
   * 修改用户信息
   * @param field
   * @param value
   */
  public async update(field: keyof IUserInfo, value: string | number): Promise<boolean> {
    try {
      await this.trpcPost('UpdateUserInfo', {
        userInfo: {
          [field]: value,
          buglyId: userImplement.getUserId(),
        },
      });
      return true;
    } catch (e) {
      return false;
    }
  }

  /**
   * 获取登录用户产品列表
   */
  public getProductList = async (): Promise<ProductInfo[]> => {
    try {
      const res = await this.trpcPost('GetProductListByUser');

      return res.productInfos || [];
    } catch (e) {
      return [];
    }
  };

  /**
   * 发送手机/邮箱验证码
   * @param req
   * @param token
   */
  public sendVerifyCode = async (req: ISendVerifyCodeReq, token?: string): Promise<boolean> => {
    const config = token ? {
      headers: {
        Authorization: token,
      },
    } : undefined;
    try {
      await this.trpcPost('SendVerifyCode', req, config);
      return true;
    } catch (e) {
      return false;
    }
  };

  /**
   * 校验并修改手机/邮箱
   * @param req
   * @param token
   */
  public verifyCode = async (req: IVerifyCodeReq, token?: string): Promise<boolean> => {
    const config = token ? {
      headers: {
        Authorization: token,
      },
    } : undefined;
    try {
      const res = await this.trpcPost('VerifyCode', req, config);

      return get(res, 'verification', false);
    } catch (e) {
      return false;
    }
  };

  /**
   * 判断用户是否是当前产品管理员
   * @param req
   */
  public checkIsAdmin = async (req: ICheckAdminReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost('CheckAdmin', req);

      return get(res, 'isAdmin', false);
    } catch (e) {
      return false;
    }
  };

  /**
   * 判断用户是否是超级管理员
   * @param req
   */
  public checkSuperAdmin = async (req: ICheckSuperAdminReq): Promise<boolean> => {
    try {
      const res = await this.trpcPost('CheckSuperAdmin', req);

      return get(res, 'isSuperAdmin', false);
    } catch (e) {
      return false;
    }
  };

  /**
   * 用户是否有某个产品权限
   * 仅限外部版本使用
   * @param req
   */
  public async canAccessProduct(req: ICheckUserInProductReq) {
    try {
      const res = await this.trpcPost('CheckUserInProduct', req);
      return get(res, 'isAuth', false);
    } catch (e) {
      return false;
    }
  }

  /**
   * 按用户名模糊搜索接口
   */
  public async search(req: IGetUsersReq): Promise<IGetUsersRsp | null> {
    try {
      return await this.trpcPost('GetUsers', req);
    } catch (e) {
      return null;
    }
  }

  /**
   * 退出产品
   * @param req
   */
  public quit = async (req: IQuitProductReq): Promise<boolean> => {
    try {
      await this.trpcPost('QuitProduct', req);

      return true;
    } catch (e) {
      return false;
    }
  };
}

export const userService = new UserService();
